<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import { useI18n } from 'vue-i18n'
import { Roles } from '@/constants.ts'
import registrationsHttpService from '@/services/http/registrationsHttpService.ts'
import eventsHttpService from '@/services/http/eventsHttpService.ts'
import { formatCategoryLabel } from '@/utils/formatCategoryLabel.ts'
import RegistrationsToolbar from './RegistrationsToolbar.vue'
import ApplicationsKanban from './ApplicationsKanban.vue'
import ApplicationsTable from './ApplicationsTable.vue'
import { toIsoDate, type RegistrationsFilters, type ViewMode } from './registrationsFilters.ts'

definePage({
  meta: {
    allowedRoles: [Roles.Admin, Roles.Manager],
  },
})

const CONFIRMED = 'Confirmed'
const CANCELLED = 'Cancelled'

const { t, locale } = useI18n()
const toast = useToast()
const queryClient = useQueryClient()

const view = ref<ViewMode>('kanban')

const viewOptions = computed(() => [
  { label: t('registrations.view.kanban'), value: 'kanban' },
  { label: t('registrations.view.table'), value: 'table' },
])

// Локальный стейт выгрузки — каждая кнопка дизейблится/крутится только на время своего запроса.
const exporting = ref(false)
const exportingPdf = ref(false)

async function onExport() {
  exporting.value = true
  try {
    await registrationsHttpService.exportRegistrations()
  } catch {
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('registrations.toasts.exportFailed'),
      life: 5000,
    })
  } finally {
    exporting.value = false
  }
}

async function onExportPdf() {
  exportingPdf.value = true
  try {
    await registrationsHttpService.exportRegistrationsPdf()
  } catch {
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('registrations.toasts.exportPdfFailed'),
      life: 5000,
    })
  } finally {
    exportingPdf.value = false
  }
}

// Реестр антивирус-проверок (Документы→Безопасность) ссылается сюда с ?search=<ФИО> —
// подхватываем как начальное значение поиска, чтобы переход сразу показывал заявки участника.
const route = useRoute()
const search = ref(typeof route.query.search === 'string' ? route.query.search : '')
const searchDebounced = ref(search.value)
let searchTimer: ReturnType<typeof setTimeout> | undefined
// Debounce, чтобы не дёргать API на каждый символ.
watch(search, (value) => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    searchDebounced.value = value
  }, 300)
})

// Отдельный поиск по нагрудному номеру (с ведущим 0 — точно, иначе по вхождению; логика на бэкенде).
const searchBib = ref('')
const searchBibDebounced = ref('')
let searchBibTimer: ReturnType<typeof setTimeout> | undefined
watch(searchBib, (value) => {
  clearTimeout(searchBibTimer)
  searchBibTimer = setTimeout(() => {
    searchBibDebounced.value = value
  }, 300)
})

const statusCode = ref<string | null>(null)
const categoryId = ref<string | null>(null)
const dateRange = ref<Date[] | null>(null)
// Оба типа выбраны по умолчанию (= фильтр не применяется).
const applicationTypes = ref<string[]>(['Individual', 'Team'])

const filters = computed<RegistrationsFilters>(() => ({
  search: searchDebounced.value.trim() || undefined,
  bib: searchBibDebounced.value.trim() || undefined,
  statusCode: statusCode.value || undefined,
  categoryId: categoryId.value || undefined,
  from: toIsoDate(dateRange.value?.[0]),
  to: toIsoDate(dateRange.value?.[1]),
  // Оба → не фильтруем; один → фильтр по нему; ноль → пустой массив (ничего не показываем).
  applicationTypes: applicationTypes.value.length === 2 ? undefined : applicationTypes.value,
}))

// Категории текущего события — опции фильтра; подпись композируется как на лендинге.
const currentEventQuery = useQuery({
  queryKey: computed(() => ['events', 'current', locale.value]),
  queryFn: () => eventsHttpService.getCurrentEvent(),
})
const categoryOptions = computed(() =>
  (currentEventQuery.data.value?.categories ?? []).map((c) => ({
    value: c.id,
    label:
      c.name ??
      formatCategoryLabel(
        {
          categoryDistanceKm: c.distanceKm,
          categoryGenderCode: c.genderCode,
          categoryAgeMin: c.ageMin,
          categoryAgeMax: c.ageMax,
          categoryIsGroupRace: c.isGroupRace,
        },
        t,
      ),
  })),
)

// locale в ключе: справочники локализуются бэком по X-Language — смена языка = рефетч.
const statusesQuery = useQuery({
  queryKey: computed(() => ['dictionaries', 'statuses', locale.value]),
  queryFn: () => registrationsHttpService.getStatuses(),
})

const statusOptions = computed(() =>
  (statusesQuery.data.value ?? []).map((s) => ({ label: s.name ?? s.code, value: s.code })),
)

// Общий выбор строк — переживает переключение вида (выбор делается в таблице).
const selectedIds = ref<string[]>([])

function invalidate() {
  queryClient.invalidateQueries({ queryKey: ['admin', 'applications'] })
  queryClient.invalidateQueries({ queryKey: ['admin', 'registrations'] })
}

// Тост результата bulk: «Обновлено N, пропущено M» — пропуск (невалидный переход) это норма.
function reportBulkResult(updated: number, skipped: number) {
  toast.add({
    severity: skipped > 0 ? 'warn' : 'success',
    summary: t('registrations.toasts.bulkResultTitle'),
    detail: t('registrations.toasts.bulkResultDetail', { updated, skipped }),
    life: 5000,
  })
}

const bulkAdmitMutation = useMutation({
  mutationFn: (ids: string[]) =>
    registrationsHttpService.bulkTransitionApplications(ids, CONFIRMED),
  onSuccess: (result) => {
    selectedIds.value = []
    reportBulkResult(result.updated, result.skipped.length)
    invalidate()
  },
  onError: () => {
    toast.add({
      severity: 'warn',
      summary: t('registrations.toasts.transitionFailedTitle'),
      detail: t('registrations.toasts.transitionRetry'),
      life: 5000,
    })
    invalidate()
  },
})

const bulkCancelMutation = useMutation({
  mutationFn: (ids: string[]) =>
    registrationsHttpService.bulkTransitionApplications(ids, CANCELLED),
  onSuccess: (result) => {
    selectedIds.value = []
    reportBulkResult(result.updated, result.skipped.length)
    invalidate()
  },
  onError: () => {
    toast.add({
      severity: 'warn',
      summary: t('registrations.toasts.transitionFailedTitle'),
      detail: t('registrations.toasts.transitionRetry'),
      life: 5000,
    })
    invalidate()
  },
})

function onBulkAdmit() {
  if (selectedIds.value.length === 0) return
  bulkAdmitMutation.mutate([...selectedIds.value])
}

// Массовая отмена каскадит на участников команд — предупреждаем перед запуском.
const confirm = useConfirm()
function onBulkCancel() {
  if (selectedIds.value.length === 0) return
  confirm.require({
    message: t('registrations.confirm.bulkCancel', { n: selectedIds.value.length }),
    header: t('registrations.bulkCancel'),
    icon: 'pi pi-exclamation-triangle',
    rejectProps: { label: t('common.cancel'), severity: 'secondary', outlined: true },
    acceptProps: { label: t('registrations.bulkCancel'), severity: 'danger' },
    accept: () => bulkCancelMutation.mutate([...selectedIds.value]),
  })
}
</script>

<template>
  <!-- Внутренний скролл: header + тулбар с фильтрами статичны сверху (закреплены), список
       (канбан/таблица) заполняет остаток высоты и прокручивается внутри со sticky-заголовками. -->
  <section class="flex flex-col gap-4 w-full flex-1 min-h-0">
    <!-- Шапка: заголовок слева, действия (выгрузка + переключатель вида) — справа, в одну линию.
         Мобайл (<md): подписи кнопок выгрузки скрыты (иконки), переключатель вида переносится
         на отдельную строку во всю ширину (w-full). -->
    <div class="flex flex-wrap items-center gap-3 w-full">
      <h1 class="text-xl font-bold mr-auto">{{ t('registrations.title') }}</h1>
      <div class="flex items-center gap-2">
        <Button
          class="btn-excel btn-export"
          size="small"
          :label="t('registrations.exportExcel')"
          icon="pi pi-file-excel"
          :loading="exporting"
          :disabled="exporting"
          data-test="export-excel"
          @click="onExport"
        />
        <Button
          class="btn-pdf btn-export"
          size="small"
          :label="t('registrations.exportPdf')"
          icon="pi pi-file-pdf"
          :loading="exportingPdf"
          :disabled="exportingPdf"
          data-test="export-pdf"
          @click="onExportPdf"
        />
      </div>
      <SelectButton
        v-model="view"
        class="view-toggle w-full md:w-auto"
        size="small"
        :options="viewOptions"
        option-label="label"
        option-value="value"
        :allow-empty="false"
        data-test="view-toggle"
      />
    </div>

    <RegistrationsToolbar
      v-model:search="search"
      v-model:search-bib="searchBib"
      v-model:status-code="statusCode"
      v-model:category-id="categoryId"
      v-model:date-range="dateRange"
      v-model:application-types="applicationTypes"
      :status-options="statusOptions"
      :category-options="categoryOptions"
      :selected-count="selectedIds.length"
      :bulk-admit-loading="bulkAdmitMutation.isPending.value"
      :bulk-cancel-loading="bulkCancelMutation.isPending.value"
      @bulk-admit="onBulkAdmit"
      @bulk-cancel="onBulkCancel"
      @clear-selection="selectedIds = []"
    />

    <ApplicationsKanban v-if="view === 'kanban'" class="flex-1 min-h-0" :filters="filters" />
    <ApplicationsTable
      v-else
      v-model:selected-ids="selectedIds"
      class="flex-1 min-h-0"
      :filters="filters"
    />
  </section>
</template>

<style scoped>
/* Outlined: прозрачный фон, граница и текст — фирменным зелёным Excel (ненавязчиво). */
.btn-excel {
  background: transparent !important;
  border-color: #217346 !important;
  color: #217346 !important;
}
.btn-excel:hover {
  background: rgba(33, 115, 70, 0.08) !important;
  border-color: #217346 !important;
}
/* Outlined: прозрачный фон, граница и текст — Adobe-красным (#FA0F00). */
.btn-pdf {
  background: transparent !important;
  border-color: #fa0f00 !important;
  color: #fa0f00 !important;
}
.btn-pdf:hover {
  background: rgba(250, 15, 0, 0.08) !important;
  border-color: #fa0f00 !important;
}

/* Мобайл (<md): кнопки выгрузки — только иконки (подпись съедала строку и выдавливала
   переключатель вида за край). На десктопе подписи остаются. */
@media (max-width: 767px) {
  .btn-export :deep(.p-button-label) {
    display: none;
  }
  /* Переключатель Канбан/Таблица во всю ширину строки — сегменты делят её поровну. */
  .view-toggle :deep(.p-togglebutton) {
    flex: 1 1 0;
  }
}
</style>
